import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { fmtErr } from "../lib/api";
import { toast } from "sonner";
import { PromoBanner } from "../components/PromoBanner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [role, setRole] = useState(sp.get("role") === "handyman" ? "handyman" : "student");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", campus: "", website: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  // Move-in Pros are UW-Madison only during launch. Force the campus field for that role.
  useEffect(() => {
    if (role === "handyman" && form.campus !== "UW-Madison") {
      setForm((f) => ({ ...f, campus: "UW-Madison" }));
    }
  }, [role, form.campus]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const googleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    sessionStorage.setItem("hm_pending_role", role);
    const redirectUrl = window.location.origin + (role === "handyman" ? "/handyman" : "/dashboard");
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = await register({ ...form, role });
      toast.success(`Welcome, ${u.name.split(" ")[0]}!`);
      nav(role === "handyman" ? "/handyman" : "/browse");
    } catch (e) {
      setErr(fmtErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center px-4 py-12 bg-cream">
      {role === "handyman" && <PromoBanner />}
      <div className="w-full max-w-lg bg-[#131C2E] rounded-2xl border border-[#1F2A40] p-8 shadow-sm animate-fade-up">
        <h1 className="font-display text-3xl text-charcoal">Create your account</h1>
        <p className="text-charcoal/60 mt-1 mb-6">Join in under a minute.</p>

        <div className="grid grid-cols-2 gap-3 mb-5">
          <button type="button" onClick={() => setRole("student")} data-testid="role-student"
            className={`rounded-xl border p-4 text-left transition-all ${role === "student" ? "border-terracotta bg-terracotta/5" : "border-[#1F2A40] hover:border-charcoal/30"}`}>
            <p className="font-display text-lg">Student / Parent</p>
            <p className="text-xs text-charcoal/60 mt-1">I need help moving in</p>
          </button>
          <button type="button" onClick={() => setRole("handyman")} data-testid="role-handyman"
            className={`rounded-xl border p-4 text-left transition-all ${role === "handyman" ? "border-terracotta bg-terracotta/5" : "border-[#1F2A40] hover:border-charcoal/30"}`}>
            <p className="font-display text-lg">Move-in Pro</p>
            <p className="text-xs text-charcoal/60 mt-1">I want to offer services</p>
          </button>
        </div>

        <Button type="button" onClick={googleSignup} variant="outline" data-testid="google-signup-btn"
          className="w-full rounded-full border-charcoal/20 hover:bg-beige mb-4">
          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.83z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
          Continue with Google as {role}
        </Button>
        <div className="flex items-center gap-3 my-4">
          <div className="h-px bg-line flex-1" />
          <span className="text-xs uppercase tracking-widest text-charcoal/50">or with email</span>
          <div className="h-px bg-line flex-1" />
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label>Full name</Label>
            <Input required value={form.name} onChange={set("name")} data-testid="reg-name" className="mt-1 rounded-xl h-11" />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" required value={form.email} onChange={set("email")} data-testid="reg-email" className="mt-1 rounded-xl h-11" />
          </div>
          <div>
            <Label>Password</Label>
            <Input type="password" required minLength={6} value={form.password} onChange={set("password")} data-testid="reg-password" className="mt-1 rounded-xl h-11" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Phone (optional)</Label>
              <Input value={form.phone} onChange={set("phone")} data-testid="reg-phone" className="mt-1 rounded-xl h-11" />
            </div>
            <div>
              <Label>Campus / City</Label>
              {role === "handyman" ? (
                <>
                  <Input
                    value="UW-Madison"
                    disabled
                    data-testid="reg-campus"
                    className="mt-1 rounded-xl h-11 opacity-80"
                  />
                  <p className="text-[11px] text-charcoal/60 mt-1">
                    Move-in Pros are UW-Madison only during launch. Contact support to request another campus.
                  </p>
                </>
              ) : (
                <Input value={form.campus} onChange={set("campus")} data-testid="reg-campus" className="mt-1 rounded-xl h-11" placeholder="e.g. UW-Madison, NYU" />
              )}
            </div>
          </div>
          {err && <p className="text-sm text-destructive" data-testid="reg-error">{err}</p>}
          {/* Honeypot — hidden from real users, catches bots that fill every field */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px", width: 1, height: 1, overflow: "hidden" }}>
            <label>Website (leave blank)
              <input type="text" tabIndex={-1} autoComplete="off"
                value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
            </label>
          </div>
          <Button type="submit" disabled={loading} data-testid="reg-submit"
            className="w-full rounded-full h-11 bg-terracotta hover:bg-terracotta/90 text-white btn-lift">
            {loading ? "Creating account…" : "Create account"}
          </Button>
        </form>
        <p className="text-sm text-charcoal/60 mt-5 text-center">
          Already have one? <Link to="/login" className="text-terracotta hover:underline" data-testid="go-login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
